import { Injectable } from '@angular/core';

import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, interval, Subject, Subscription } from 'rxjs';
import moment from 'moment';
import { apiUrls, MarketCloseTime, MarketOpenTime, ShowMarketOpenTime } from '../../../constants';
import { BaseService } from '@easy-lib/modules/shared/services/Base/base.service';
import { DateHelper } from '@easy-lib/tools';

@Injectable({
	providedIn: 'root'
})
export class TimeService extends BaseService {
	private initialTime = new Date().toLocaleTimeString([], {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false
	});
	private clock = new Subject<number>();
	private isMarketDaySub = new BehaviorSubject<boolean>(true);

	private serverTimeSubject = new BehaviorSubject<string>(this.initialTime);
	serverTimeObs$ = this.serverTimeSubject.asObservable();

	isMarketOpenSub = new BehaviorSubject<boolean>(null);
	isMarketOpen$ = this.isMarketOpenSub.asObservable();
	showMarketOpenTimeSub = new BehaviorSubject<boolean>(null);

	public serverDateSub = new BehaviorSubject<Date>(new Date());
	public serverDateObs$ = this.serverDateSub.asObservable();

	private clockSubscription: Subscription;
	constructor() {
		super();
		this.setTime();
	}
	setTime() {
		this.clockSubscription = this.addSeconds(moment());
		this.getServerTime();
		this.getStartSession();
		interval(60000)
			.pipe(
				tap(() => {
					this.getServerTime();
				})
			)
			.subscribe();

		interval(1800000)
			.pipe(
				tap(() => {
					if (this.isMarketDaySub.value === false) this.getStartSession();
				})
			)
			.subscribe();
	}

	private getServerTime() {
		const clientTimestamp = new Date().valueOf();
		this.httpService
			.get<{
				diff: number;
				serverTimestamp: number;
			}>(`${this.mtsPath + apiUrls.easy}account/server-time/${clientTimestamp}`)
			.subscribe(res => {
				const nowTimeStamp = new Date();
				const localOffset = nowTimeStamp.getTimezoneOffset() * 60000;
				const serverClientRequestDiffTime = res.diff;

				const serverTimestamp = res.serverTimestamp;
				const serverClientResponseDiffTime = nowTimeStamp.valueOf() - serverTimestamp;
				const responseTime =
					(serverClientRequestDiffTime -
						nowTimeStamp.valueOf() +
						clientTimestamp -
						serverClientResponseDiffTime) /
					2;
				const result = DateHelper.utcToTehran(
					serverTimestamp.valueOf() + (serverClientResponseDiffTime + responseTime),
					localOffset
				);

				if (this.clockSubscription) this.clockSubscription.unsubscribe();

				this.serverDateSub.next(new Date(result));
				const clockTime = moment(result).locale('en').format('HH:mm:ss');
				this.serverTimeSubject.next(clockTime);
				this.clockSubscription = this.addSeconds(moment(result));
			});
	}
	private setIsMarketOpen(clock: string) {
		const hm = clock.split(':');
		const time = +(hm[0] + hm[1]);
		const timeIsTimeOfMarket = time >= MarketOpenTime && time < MarketCloseTime;
		const showMarketOpenTime = time >= ShowMarketOpenTime && time < MarketCloseTime;
		if (this.isMarketDaySub.value !== null) {
			this.isMarketOpenSub.next(timeIsTimeOfMarket && this.isMarketDaySub.value);
			this.showMarketOpenTimeSub.next(showMarketOpenTime && this.isMarketDaySub.value);
		}
	}

	private addSeconds(time) {
		return interval(1000)
			.pipe(
				map(() => {
					this.setIsMarketOpen(time.locale('en').format('HH:mm:ss'));
					this.serverTimeSubject.next(time.add(1, 'seconds').locale('en').format('HH:mm:ss'));
				})
			)
			.subscribe();
	}
	private getStartSession() {
		this.httpService
			.get<{ serverTime: Date; startSessionTimeStamp: Date }>(`${this.mtsPath + apiUrls.easy}startsession`)
			.subscribe(res => {
				if (
					new Date(res.serverTime).setHours(0, 0, 0, 0) ===
					new Date(res.startSessionTimeStamp).setHours(0, 0, 0, 0)
				) {
					this.isMarketDaySub.next(true);
				} else {
					this.isMarketDaySub.next(false);
				}
			});
	}
}
